/**
 * Create-MCP-token dialog (Block 4.3), reachable from the file-list MCP-Token
 * column on a folder. The plaintext bearer is shown exactly once, with a copy
 * button and a clear warning; afterwards only its hash is stored.
 */

import { useState } from "react";

import { ApiError, ryfeApi } from "../api";

import "./ryfe.scss";

import type { AclRole } from "../api";

export const CreateTokenDialog = ({
  wsId,
  folderId,
  folderName,
  onClose,
  onCreated,
}: {
  wsId: string;
  folderId: string;
  folderName: string;
  onClose: () => void;
  onCreated: () => void;
}) => {
  const [name, setName] = useState("");
  const [role, setRole] = useState<AclRole>("editor");
  const [expiry, setExpiry] = useState("never");
  const [days, setDays] = useState("30");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await ryfeApi.createMcpToken(wsId, folderId, {
        name,
        role,
        expiresInDays: expiry === "days" ? Number(days) : null,
      });
      setToken(res.token);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not create token",
      );
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!token) {
      return;
    }
    try {
      await navigator.clipboard?.writeText(token);
      setCopied(true);
    } catch {
      /* clipboard optional */
    }
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        {token ? (
          <>
            <h2 className="ryfe-panel__title">Token erstellt</h2>
            <div className="ryfe-notice ryfe-notice--warn">
              Dieser Token wird <strong>nur jetzt</strong> angezeigt. Kopiere
              ihn und bewahre ihn sicher auf — danach ist er nicht mehr
              abrufbar.
            </div>
            <div className="ryfe-token-box">
              <code>{token}</code>
            </div>
            <div className="ryfe-modal__actions">
              <button className="ryfe-btn ryfe-btn--ghost" onClick={copy}>
                {copied ? "Kopiert ✓" : "Kopieren"}
              </button>
              <span className="ryfe-topbar__spacer" />
              <button
                className="ryfe-btn ryfe-btn--primary"
                onClick={onCreated}
              >
                Fertig
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={create}>
            <h2 className="ryfe-panel__title">MCP-Token für „{folderName}“</h2>
            <label className="ryfe-field">
              <span>Name / Zweck</span>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </label>
            <label className="ryfe-field">
              <span>Rolle</span>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as AclRole)}
              >
                <option value="editor">editor</option>
                <option value="viewer">viewer</option>
              </select>
            </label>
            <label className="ryfe-field">
              <span>Ablauf</span>
              <select
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
              >
                <option value="never">läuft nie ab</option>
                <option value="days">in N Tagen…</option>
              </select>
            </label>
            {expiry === "days" && (
              <label className="ryfe-field">
                <span>Tage</span>
                <input
                  type="number"
                  min={1}
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                />
              </label>
            )}
            {error && (
              <div className="ryfe-auth__error" role="alert">
                {error}
              </div>
            )}
            <div className="ryfe-modal__actions">
              <span className="ryfe-topbar__spacer" />
              <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
                Abbrechen
              </button>
              <button
                className="ryfe-btn ryfe-btn--primary"
                type="submit"
                disabled={busy}
              >
                Erstellen
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
